import {
  CalendarRange,
  Check,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "./Feedback";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import type {
  EmploymentRelationship,
  OrganizationMember,
  Paginated,
} from "../types/api";
import { unwrapResults } from "../types/api";
import { apiErrorMessage } from "../utils/apiError";
import { formatDate, formatMinutes } from "../utils/format";
import DateField from "./DateField";

type ShiftSystem = "2_shift" | "3_shift" | "5_shift" | "custom";
type ScheduleDay = {
  weekday: number | null;
  cycle_day: number | null;
  minutes: number;
  shift_label: string;
};
type Schedule = {
  id: number;
  user: number;
  organization: number | null;
  employment: number | null;
  effective_from: string;
  effective_until: string | null;
  label: string;
  schedule_type: "weekly" | "rotation";
  shift_work_enabled: boolean;
  shift_system: "none" | ShiftSystem;
  cycle_anchor_date: string | null;
  cycle_length_days: number | null;
  total_minutes_per_week: number;
  days: ScheduleDay[];
};
type ScheduleOverride = {
  id: number;
  employment: number | null;
  date: string;
  minutes: number;
  shift_label: string;
  reason: string;
};
type CycleDraft = { label: string; hours: string };

const dayLabels = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const longDayLabels = [
  "Maandag",
  "Dinsdag",
  "Woensdag",
  "Donderdag",
  "Vrijdag",
  "Zaterdag",
  "Zondag",
];

function isoDate(value: Date) {
  return value.toISOString().slice(0, 10);
}

function parseDate(value: string) {
  return new Date(`${value}T12:00:00`);
}

function addDays(value: string, days: number) {
  const result = parseDate(value);
  result.setDate(result.getDate() + days);
  return isoDate(result);
}

function weekStart(value = isoDate(new Date())) {
  const result = parseDate(value);
  const weekday = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - weekday);
  return isoDate(result);
}

function repeated(label: string, count: number): CycleDraft[] {
  return Array.from({ length: count }, () => ({
    label,
    hours: label === "Vrij" ? "0" : "8",
  }));
}

function shiftPreset(system: ShiftSystem): CycleDraft[] {
  if (system === "2_shift") {
    return [
      ...repeated("Ochtend", 5),
      ...repeated("Vrij", 2),
      ...repeated("Middag", 5),
      ...repeated("Vrij", 2),
    ];
  }
  if (system === "3_shift") {
    return [
      ...repeated("Ochtend", 5),
      ...repeated("Vrij", 2),
      ...repeated("Middag", 5),
      ...repeated("Vrij", 2),
      ...repeated("Nacht", 5),
      ...repeated("Vrij", 2),
    ];
  }
  if (system === "5_shift") {
    return [
      ...repeated("Ochtend", 2),
      ...repeated("Middag", 2),
      ...repeated("Nacht", 2),
      ...repeated("Vrij", 4),
    ];
  }
  return repeated("Dienst", 7);
}

export default function WorkScheduleSettings({
  scopedEmploymentId,
}: {
  scopedEmploymentId?: number | null;
}) {
  const { user } = useAuth();
  const { organizationId, capabilities } = useWorkspace();
  const [targetUser, setTargetUser] = useState<number | null>(user?.id ?? null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [employments, setEmployments] = useState<EmploymentRelationship[]>([]);
  const [employmentId, setEmploymentId] = useState<number | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [overrides, setOverrides] = useState<ScheduleOverride[]>([]);
  const [effectiveFrom, setEffectiveFrom] = useState(isoDate(new Date()));
  const [weeklyHours, setWeeklyHours] = useState(["8", "8", "4", "8", "8", "0", "0"]);
  const [shiftEnabled, setShiftEnabled] = useState(false);
  const [shiftSystem, setShiftSystem] = useState<ShiftSystem>("2_shift");
  const [cycleDays, setCycleDays] = useState<CycleDraft[]>(shiftPreset("2_shift"));
  const [selectedWeek, setSelectedWeek] = useState(weekStart());
  const [weekValues, setWeekValues] = useState<string[]>(Array(7).fill(""));
  const [weekLabels, setWeekLabels] = useState<string[]>(Array(7).fill(""));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingScheduleId, setDeletingScheduleId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canEdit =
    !organizationId ||
    (capabilities.canManageOrganizationPolicies && !capabilities.organizationReadOnly);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (organizationId) params.set("organization", String(organizationId));
    if (targetUser) params.set("user", String(targetUser));
    if (!organizationId && employmentId) {
      params.set("employment", String(employmentId));
    }
    return params.toString();
  }, [employmentId, organizationId, targetUser]);

  useEffect(() => {
    setTargetUser(user?.id ?? null);
    setMembers([]);
    setEmployments([]);
    setEmploymentId(null);
  }, [organizationId, user?.id]);

  useEffect(() => {
    if (scopedEmploymentId != null) {
      setEmploymentId(scopedEmploymentId);
    }
  }, [scopedEmploymentId]);

  useEffect(() => {
    if (!organizationId || !capabilities.canManageOrganizationPolicies) return;
    void api
      .get<OrganizationMember[]>(`/organizations/${organizationId}/members/`)
      .then((response) =>
        setMembers(response.data.filter((member) => member.status === "active")),
      )
      .catch(() => setError("De medewerkers konden niet worden geladen."));
  }, [capabilities.canManageOrganizationPolicies, organizationId]);

  useEffect(() => {
    if (!targetUser || organizationId || scopedEmploymentId != null) return;
    const params = new URLSearchParams();
    if (targetUser) params.set("user", String(targetUser));
    const suffix = params.toString() ? `?${params}` : "";
    void api
      .get<Paginated<EmploymentRelationship> | EmploymentRelationship[]>(
        `/organizations/employments/${suffix}`,
      )
      .then((response) => {
        const rows = unwrapResults(response.data);
        setEmployments(rows);
        setEmploymentId((current) =>
          current && rows.some((row) => row.id === current)
            ? current
            : rows[0]?.id ?? null,
        );
      })
      .catch(() => setError("De werkgevers konden niet worden geladen."));
  }, [organizationId, scopedEmploymentId, targetUser]);

  const loadSchedules = useCallback(async () => {
    if (!targetUser) return;
    setLoading(true);
    try {
      const suffix = query ? `?${query}` : "";
      const response = await api.get<Paginated<Schedule> | Schedule[]>(
        `/leave/schedule-versions/${suffix}`,
      );
      setSchedules(unwrapResults(response.data));
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De werkpatronen konden niet worden geladen."),
      );
    } finally {
      setLoading(false);
    }
  }, [query, targetUser]);

  const loadOverrides = useCallback(async () => {
    if (!targetUser) return;
    const params = new URLSearchParams(query);
    params.set("date_from", selectedWeek);
    params.set("date_to", addDays(selectedWeek, 6));
    try {
      const response = await api.get<Paginated<ScheduleOverride> | ScheduleOverride[]>(
        `/leave/schedule-overrides/?${params}`,
      );
      const rows = unwrapResults(response.data);
      setOverrides(rows);
      const byDate = new Map(rows.map((row) => [row.date, row]));
      setWeekValues(
        Array.from({ length: 7 }, (_, index) => {
          const row = byDate.get(addDays(selectedWeek, index));
          return row ? String(row.minutes / 60) : "";
        }),
      );
      setWeekLabels(
        Array.from(
          { length: 7 },
          (_, index) => byDate.get(addDays(selectedWeek, index))?.shift_label ?? "",
        ),
      );
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De weekafwijkingen konden niet worden geladen."),
      );
    }
  }, [query, selectedWeek, targetUser]);

  useEffect(() => {
    void loadSchedules();
  }, [loadSchedules]);

  useEffect(() => {
    void loadOverrides();
  }, [loadOverrides]);

  const weeklyMinutes = weeklyHours.reduce(
    (sum, value) => sum + Math.round((Number(value) || 0) * 60),
    0,
  );
  const cycleAverageMinutes = Math.round(
    (cycleDays.reduce(
      (sum, day) => sum + Math.round((Number(day.hours) || 0) * 60),
      0,
    ) *
      7) /
      cycleDays.length,
  );
  const cycleWeeks =
    cycleDays.length % 7 === 0 && cycleDays.length / 7 <= 4
      ? cycleDays.length / 7
      : null;

  const chooseShiftSystem = (value: ShiftSystem) => {
    setShiftSystem(value);
    setCycleDays(shiftPreset(value));
  };

  const resizeCycle = (length: number) => {
    const bounded = Math.min(56, Math.max(2, length || 2));
    setCycleDays((current) =>
      Array.from(
        { length: bounded },
        (_, index) => current[index] ?? { label: "Dienst", hours: "8" },
      ),
    );
    setShiftSystem("custom");
  };

  const openNewSchedule = () => {
    setEditingSchedule(null);
    setEffectiveFrom(isoDate(new Date()));
    setWeeklyHours(["8", "8", "4", "8", "8", "0", "0"]);
    setShiftEnabled(false);
    setShiftSystem("2_shift");
    setCycleDays(shiftPreset("2_shift"));
    setShowScheduleForm(true);
  };

  const editSchedule = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setEffectiveFrom(schedule.effective_from);
    setShiftEnabled(schedule.schedule_type === "rotation");
    if (schedule.schedule_type === "rotation") {
      setShiftSystem(
        schedule.shift_system === "none" ? "custom" : schedule.shift_system,
      );
      setCycleDays(
        [...schedule.days]
          .sort((left, right) => (left.cycle_day ?? 0) - (right.cycle_day ?? 0))
          .map((day) => ({
            label: day.shift_label,
            hours: String(day.minutes / 60),
          })),
      );
    } else {
      setWeeklyHours(
        dayLabels.map((_, weekday) => {
          const day = schedule.days.find((item) => item.weekday === weekday);
          return String((day?.minutes ?? 0) / 60);
        }),
      );
    }
    setShowScheduleForm(true);
    setError("");
    setSuccess("");
  };

  const closeScheduleForm = () => {
    setEditingSchedule(null);
    setShowScheduleForm(false);
  };

  const removeSchedule = async (schedule: Schedule) => {
    if (!window.confirm("Dit werkpatroon verwijderen?")) return;
    setDeletingScheduleId(schedule.id);
    setError("");
    try {
      const suffix = query ? `?${query}` : "";
      await api.delete(`/leave/schedule-versions/${schedule.id}/${suffix}`);
      if (editingSchedule?.id === schedule.id) closeScheduleForm();
      setSuccess("Het werkpatroon is verwijderd.");
      await loadSchedules();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het werkpatroon kon niet worden verwijderd."),
      );
    } finally {
      setDeletingScheduleId(null);
    }
  };

  const createSchedule = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = shiftEnabled
        ? {
            organization: organizationId,
            employment: organizationId ? null : employmentId,
            user: targetUser,
            effective_from: effectiveFrom,
            label: `${shiftSystemLabel(shiftSystem)} vanaf ${formatDate(
              effectiveFrom,
            )}`,
            schedule_type: "rotation",
            shift_work_enabled: true,
            shift_system: shiftSystem,
            cycle_anchor_date: effectiveFrom,
            cycle_length_days: cycleDays.length,
            days: cycleDays.map((day, cycleDay) => ({
              weekday: null,
              cycle_day: cycleDay,
              minutes: Math.round((Number(day.hours) || 0) * 60),
              shift_label: day.label.trim(),
            })),
          }
        : {
            organization: organizationId,
            employment: organizationId ? null : employmentId,
            user: targetUser,
            effective_from: effectiveFrom,
            label: `Vaste werkweek vanaf ${formatDate(effectiveFrom)}`,
            schedule_type: "weekly",
            days: weeklyHours.map((value, weekday) => ({
              weekday,
              cycle_day: null,
              minutes: Math.round((Number(value) || 0) * 60),
              shift_label: "",
            })),
          };
      if (editingSchedule) {
        await api.patch(`/leave/schedule-versions/${editingSchedule.id}/`, payload);
      } else {
        await api.post("/leave/schedule-versions/", payload);
      }
      setSuccess(
        editingSchedule
          ? "Het werkpatroon is bijgewerkt."
          : "Het nieuwe werkpatroon is ingepland.",
      );
      setEditingSchedule(null);
      setShowScheduleForm(false);
      await loadSchedules();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het werkpatroon kon niet worden opgeslagen."),
      );
    } finally {
      setSaving(false);
    }
  };

  const saveWeek = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const existingByDate = new Map(overrides.map((row) => [row.date, row]));
      await Promise.all(
        weekValues.map(async (value, index) => {
          const day = addDays(selectedWeek, index);
          const existing = existingByDate.get(day);
          const suffix = query ? `?${query}` : "";
          if (value === "") {
            if (existing) {
              await api.delete(`/leave/schedule-overrides/${existing.id}/${suffix}`);
            }
            return;
          }
          const payload = {
            organization: organizationId,
            employment: organizationId ? null : employmentId,
            user: targetUser,
            date: day,
            minutes: Math.round((Number(value) || 0) * 60),
            shift_label: weekLabels[index].trim(),
            reason: "Variabel werkrooster",
          };
          if (existing) {
            await api.patch(
              `/leave/schedule-overrides/${existing.id}/${suffix}`,
              payload,
            );
          } else {
            await api.post("/leave/schedule-overrides/", payload);
          }
        }),
      );
      setSuccess("De uren voor deze week zijn opgeslagen.");
      await loadOverrides();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De weekuren konden niet worden opgeslagen."),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label="Werkpatronen laden..." />;

  return (
    <div className="settings-stack">
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      {(members.length > 0 || (!organizationId && employments.length > 0)) && (
        <section className="surface settings-section settings-context-bar">
          {members.length > 0 && (
            <label>
              <span>Medewerker</span>
              <select
                value={targetUser ?? ""}
                onChange={(event) => {
                  setTargetUser(Number(event.target.value));
                  setEmploymentId(null);
                }}
              >
                {members.map((member) => (
                  <option key={member.user} value={member.user}>
                    {member.display_name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!organizationId && employments.length > 0 && scopedEmploymentId == null && (
            <label>
              <span>Werkgever</span>
              <select
                value={employmentId ?? ""}
                onChange={(event) => setEmploymentId(Number(event.target.value))}
              >
                {employments.map((employment) => (
                  <option key={employment.id} value={employment.id}>
                    {employment.display_name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </section>
      )}

      <section className="surface settings-section">
        <div className="settings-heading settings-heading-with-action">
          <div>
            <h2>Werkpatroonhistorie</h2>
            <p>
              Elke wijziging heeft een ingangsdatum; eerdere berekeningen blijven
              intact.
              {!organizationId &&
                employments.length > 0 &&
                " Het patroon geldt voor de gekozen werkgever."}
            </p>
          </div>
          {canEdit && schedules.length > 0 && (
            <button className="button subtle" type="button" onClick={openNewSchedule}>
              <Plus size={17} /> Werkpatroon toevoegen
            </button>
          )}
        </div>
        <div className="version-list">
          {schedules.length === 0 && (
            <p className="empty-copy">Nog geen versieerbaar werkpatroon ingesteld.</p>
          )}
          {schedules.map((schedule) => (
            <article className="schedule-version" key={schedule.id}>
              <div className="schedule-version-header">
                <span>
                  <strong>{schedule.label || "Werkpatroon"}</strong>
                  <small>
                    {formatDate(schedule.effective_from)}
                    {schedule.effective_until
                      ? ` t/m ${formatDate(schedule.effective_until)}`
                      : " - doorlopend"}
                    {schedule.shift_work_enabled && schedule.cycle_length_days
                      ? ` · cyclus van ${schedule.cycle_length_days} dagen`
                      : ""}
                    {schedule.employment
                      ? ` · ${employmentName(schedule.employment, employments)}`
                      : ""}
                  </small>
                </span>
                <span className="schedule-version-actions">
                  <strong>{formatMinutes(schedule.total_minutes_per_week)}/week</strong>
                  {canEdit && (
                    <>
                      <button
                        className="icon-button"
                        type="button"
                        title="Werkpatroon wijzigen"
                        aria-label={`${schedule.label || "Werkpatroon"} wijzigen`}
                        onClick={() => editSchedule(schedule)}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="icon-button danger"
                        type="button"
                        title="Werkpatroon verwijderen"
                        aria-label={`${schedule.label || "Werkpatroon"} verwijderen`}
                        disabled={deletingScheduleId === schedule.id}
                        onClick={() => void removeSchedule(schedule)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                </span>
              </div>
              <div
                className="schedule-version-days"
                aria-label="Verdeling van werkuren"
              >
                {schedule.schedule_type === "rotation"
                  ? schedule.days.map((day, index) => (
                      <span key={index}>
                        <small>Dag {index + 1}</small>
                        <strong>{formatMinutes(day.minutes)}</strong>
                        {day.shift_label && <em>{day.shift_label}</em>}
                      </span>
                    ))
                  : dayLabels.map((day, weekday) => {
                      const scheduleDay = schedule.days.find(
                        (item) => item.weekday === weekday,
                      );
                      return (
                        <span key={day}>
                          <small>{day}</small>
                          <strong>
                            {scheduleDay ? formatMinutes(scheduleDay.minutes) : "Vrij"}
                          </strong>
                        </span>
                      );
                    })}
              </div>
            </article>
          ))}
        </div>
      </section>

      {canEdit && (schedules.length === 0 || showScheduleForm || editingSchedule) && (
        <form className="surface settings-section" onSubmit={createSchedule}>
          <div className="settings-heading settings-heading-with-action">
            <div>
              <h2>{editingSchedule ? "Werkpatroon wijzigen" : "Nieuw basispatroon"}</h2>
              <p>
                {editingSchedule
                  ? "Werk de uren of ploegencyclus bij. De ingangsdatum blijft gelijk."
                  : "Kies een vaste werkweek of schakel een bewerkbare ploegencyclus in."}
              </p>
            </div>
            {schedules.length > 0 && (
              <button
                className="icon-button"
                type="button"
                title="Formulier sluiten"
                onClick={closeScheduleForm}
              >
                <X size={17} />
              </button>
            )}
          </div>
          <label>
            <span>Ingangsdatum</span>
            <DateField
              value={effectiveFrom}
              onChange={(value) => setEffectiveFrom(value)}
              disabled={Boolean(editingSchedule)}
              required
            />
          </label>
          <label className="toggle-field">
            <input
              type="checkbox"
              checked={shiftEnabled}
              onChange={(event) => setShiftEnabled(event.target.checked)}
            />
            <span>
              <strong>Ploegendienst gebruiken</strong>
              <small>
                Standaard uit; activeer dit alleen voor een roterende cyclus.
              </small>
            </span>
          </label>

          {!shiftEnabled ? (
            <div className="schedule-edit-grid">
              {dayLabels.map((day, index) => (
                <label key={day}>
                  <span>{day}</span>
                  <span className="number-suffix">
                    <input
                      type="number"
                      min="0"
                      max="24"
                      step="0.25"
                      value={weeklyHours[index]}
                      onChange={(event) =>
                        setWeeklyHours((current) =>
                          current.map((value, currentIndex) =>
                            currentIndex === index ? event.target.value : value,
                          ),
                        )
                      }
                    />
                    <small>uur</small>
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <>
              <div className="form-row">
                <label>
                  <span>Ploegensysteem</span>
                  <select
                    value={shiftSystem}
                    onChange={(event) =>
                      chooseShiftSystem(event.target.value as ShiftSystem)
                    }
                  >
                    <option value="2_shift">2-ploegen</option>
                    <option value="3_shift">3-ploegen</option>
                    <option value="5_shift">5-ploegen</option>
                    <option value="custom">Aangepast</option>
                  </select>
                </label>
                <label>
                  <span>Herhalingsritme</span>
                  <select
                    value={cycleWeeks ? String(cycleWeeks) : "custom"}
                    onChange={(event) => {
                      if (event.target.value === "custom") {
                        setShiftSystem("custom");
                        return;
                      }
                      resizeCycle(Number(event.target.value) * 7);
                    }}
                  >
                    <option value="1">Iedere week</option>
                    <option value="2">Iedere 2 weken</option>
                    <option value="3">Iedere 3 weken</option>
                    <option value="4">Iedere 4 weken</option>
                    <option value="custom">Aangepaste cyclus</option>
                  </select>
                </label>
                <label>
                  <span>Cycluslengte</span>
                  <span className="number-suffix">
                    <input
                      type="number"
                      min="2"
                      max="56"
                      value={cycleDays.length}
                      onChange={(event) => resizeCycle(Number(event.target.value))}
                    />
                    <small>dagen</small>
                  </span>
                </label>
              </div>
              <div className="cycle-editor" role="group" aria-label="Ploegencyclus">
                <div className="cycle-editor-head" aria-hidden="true">
                  <span>Dag</span>
                  <span>Dienst</span>
                  <span>Uren</span>
                </div>
                {cycleDays.map((day, index) => (
                  <div className="cycle-editor-row" key={index}>
                    <strong>{index + 1}</strong>
                    <label>
                      <span className="sr-only">Dienst op cyclusdag {index + 1}</span>
                      <input
                        list="shift-label-options"
                        value={day.label}
                        onChange={(event) =>
                          setCycleDays((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, label: event.target.value }
                                : item,
                            ),
                          )
                        }
                      />
                    </label>
                    <label className="number-suffix">
                      <span className="sr-only">Uren op cyclusdag {index + 1}</span>
                      <input
                        type="number"
                        min="0"
                        max="24"
                        step="0.25"
                        value={day.hours}
                        onChange={(event) =>
                          setCycleDays((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, hours: event.target.value }
                                : item,
                            ),
                          )
                        }
                      />
                      <small>uur</small>
                    </label>
                  </div>
                ))}
                <datalist id="shift-label-options">
                  <option value="Ochtend" />
                  <option value="Middag" />
                  <option value="Nacht" />
                  <option value="Vrij" />
                </datalist>
              </div>
            </>
          )}
          <div className="inline-total">
            <span>Gemiddelde werkweek</span>
            <strong>
              {formatMinutes(shiftEnabled ? cycleAverageMinutes : weeklyMinutes)}
            </strong>
          </div>
          <button className="button primary" type="submit" disabled={saving}>
            {editingSchedule ? <Check size={17} /> : <Plus size={17} />}
            {editingSchedule ? "Werkpatroon opslaan" : "Werkpatroon toevoegen"}
          </button>
        </form>
      )}

      <form className="surface settings-section" onSubmit={saveWeek}>
        <div className="balance-section-heading schedule-week-heading">
          <div>
            <h2>Variabele uren per datum</h2>
            <p>
              Leeg gebruikt het basispatroon; 0 uur legt expliciet een vrije dag vast.
            </p>
          </div>
          <div className="week-stepper">
            <button
              className="icon-button"
              type="button"
              title="Vorige week"
              onClick={() => setSelectedWeek(addDays(selectedWeek, -7))}
            >
              <ChevronLeft size={18} />
            </button>
            <label>
              <span className="sr-only">Week vanaf</span>
              <DateField
                value={selectedWeek}
                onChange={(value) => setSelectedWeek(weekStart(value))}
              />
            </label>
            <button
              className="icon-button"
              type="button"
              title="Volgende week"
              onClick={() => setSelectedWeek(addDays(selectedWeek, 7))}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
        <div className="variable-week-grid">
          {weekValues.map((value, index) => (
            <div key={index} className="variable-week-day">
              <span>
                <strong>{longDayLabels[index]}</strong>
                <small>{formatDate(addDays(selectedWeek, index))}</small>
              </span>
              <label className="number-suffix">
                <span className="sr-only">Uren</span>
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.25"
                  placeholder="Basis"
                  value={value}
                  disabled={!canEdit}
                  onChange={(event) =>
                    setWeekValues((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? event.target.value : item,
                      ),
                    )
                  }
                />
                <small>uur</small>
              </label>
              <label>
                <span className="sr-only">Dienstlabel</span>
                <input
                  placeholder="Dienstlabel"
                  value={weekLabels[index]}
                  disabled={!canEdit}
                  onChange={(event) =>
                    setWeekLabels((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? event.target.value : item,
                      ),
                    )
                  }
                />
              </label>
            </div>
          ))}
        </div>
        {canEdit && (
          <button className="button primary" type="submit" disabled={saving}>
            <Check size={17} /> Week opslaan
          </button>
        )}
        {!canEdit && (
          <p className="read-only-note">
            <CalendarRange size={17} /> Alleen een organisatiebeheerder kan dit rooster
            wijzigen.
          </p>
        )}
      </form>
    </div>
  );
}

function shiftSystemLabel(system: ShiftSystem) {
  if (system === "2_shift") return "2-ploegenrooster";
  if (system === "3_shift") return "3-ploegenrooster";
  if (system === "5_shift") return "5-ploegenrooster";
  return "Aangepaste ploegencyclus";
}

function employmentName(id: number, employments: EmploymentRelationship[]) {
  return employments.find((row) => row.id === id)?.display_name ?? "Persoonlijk";
}
